import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Send, Ban, Archive, RotateCcw, Trash2, Copy, EyeOff } from "lucide-react";
import { SectionLabel } from "./shiftUi";

export function LifecycleActions({ status, can, shift, seriesId, shiftName, doPublish, doUnpublish, openReason, runAction, onOpenChange, onDeleteSeries, onClone, onCancelSeries }) {
  const editable = !["Cancelled", "Archived", "In Progress", "Completed"].includes(status);
  const published = !!shift?.published;
  const approved = shift?.timesheet_status === "Approved";
  const invoiced = ["Invoiced", "Submitted"].includes(shift?.custom_invoice_status);
  // A shift can only be deleted while it's an unpublished draft that hasn't been
  // approved or invoiced.
  const deletable = can.has("roster.delete") && !published && !approved && !invoiced
    && !["In Progress", "Completed", "Cancelled", "Archived"].includes(status);
  return (
    <div>
      <SectionLabel>Actions</SectionLabel>
      <div className="flex flex-wrap gap-2">
        {!published && status === "Draft" && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={doPublish} data-testid="action-publish">
            <Send className="h-3.5 w-3.5 mr-1.5" /> Publish
          </Button>
        )}
        {published && editable && (
          <Button variant="outline" size="sm" className="h-8 text-xs text-amber-700 border-amber-200 hover:bg-amber-50" onClick={doUnpublish} data-testid="action-unpublish">
            <EyeOff className="h-3.5 w-3.5 mr-1.5" /> Unpublish
          </Button>
        )}
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClone} data-testid="action-clone">
          <Copy className="h-3.5 w-3.5 mr-1.5" /> Clone to date
        </Button>
        {status !== "Cancelled" && status !== "Archived" && (
          <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => seriesId && onCancelSeries ? onCancelSeries() : openReason("cancel", "Cancellation", (r, charge, initiator, notify) => api.cancel(shiftName, r, charge, initiator, notify))} data-testid="action-cancel">
            <Ban className="h-3.5 w-3.5 mr-1.5" /> Cancel
          </Button>
        )}
        {status === "Cancelled" && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => runAction(() => api.uncancel(shiftName), "Shift uncancelled")} data-testid="action-uncancel">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Uncancel
          </Button>
        )}
        {status !== "Archived" && !published && !approved && !invoiced && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => runAction(() => api.archive(shiftName), "Shift archived")} data-testid="action-archive">
            <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive
          </Button>
        )}
        {deletable && (
          <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => openReason("delete", "Delete", async () => { await api.del(shiftName); onOpenChange(false); return { closed: true }; })} data-testid="action-delete">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
          </Button>
        )}
        {seriesId && deletable && (
          <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
            onClick={onDeleteSeries} data-testid="action-delete-series">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete repeating…
          </Button>
        )}
      </div>
      {(published || approved || invoiced) && (
        <div className="text-[10px] text-slate-400 mt-1.5">
          {published ? "Unpublish to archive or delete this shift." : approved ? "Approved shifts can't be archived or deleted." : "Invoiced shifts can't be archived or deleted."}
        </div>
      )}
      {seriesId && (
        <div className="text-[10px] text-slate-400 mt-1.5" data-testid="repeat-indicator">Part of a repeating series — edits can apply to future repeats.</div>
      )}
    </div>
  );
}
